import type { IconKind } from "./types";

/**
 * Local-recents store for Web Clips added via the editor.
 *
 * Why localStorage?
 *   The user wanted "previously added web clips" surfaced again. Some of those
 *   live in saved configurations on the backend (covered by useRecentWebClips),
 *   but admins routinely add a clip, navigate away, then realize they want the
 *   same clip on a different layout. localStorage lets us suggest those without
 *   any backend round-trip and survives reloads of the editor.
 *
 * Shape
 *   We persist the *minimum* re-droppable fields plus a timestamp so the list
 *   can be sorted by recency. We also persist `iconKind` so the suggestion
 *   tile renders with the correct favicon/generic verdict immediately.
 */
export interface RecentWebClip {
  url: string;
  displayName?: string;
  icon?: string;
  iconKind?: IconKind;
  /** ms since epoch — when this clip was last added. */
  addedAt: number;
}

const STORAGE_KEY = "smplify.homescreen.webclipRecents.v1";
/** Cap so the localStorage entry stays small even after months of use. */
const MAX_RECENTS = 50;

function safeParse(raw: string | null): RecentWebClip[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentWebClip);
  } catch {
    return [];
  }
}

function isRecentWebClip(value: unknown): value is RecentWebClip {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.url !== "string" || !v.url) return false;
  if (typeof v.addedAt !== "number" || !Number.isFinite(v.addedAt)) return false;
  if (v.displayName != null && typeof v.displayName !== "string") return false;
  if (v.icon != null && typeof v.icon !== "string") return false;
  if (v.iconKind != null && v.iconKind !== "favicon" && v.iconKind !== "generic") {
    return false;
  }
  return true;
}

/** Read and validate the recents list, sorted newest-first. */
export function loadRecentWebClips(): RecentWebClip[] {
  if (typeof window === "undefined") return [];
  const list = safeParse(window.localStorage.getItem(STORAGE_KEY));
  return [...list].sort((a, b) => b.addedAt - a.addedAt);
}

/**
 * Record a new (or updated) clip. Dedupes by URL, keeps the most recent
 * verdict/displayName/icon, and bumps `addedAt` to "now".
 */
export function recordRecentWebClip(input: Omit<RecentWebClip, "addedAt"> & { addedAt?: number }): RecentWebClip[] {
  if (typeof window === "undefined") return [];
  const url = input.url?.trim();
  if (!url) return loadRecentWebClips();
  const existing = loadRecentWebClips();
  const now = input.addedAt ?? Date.now();
  // Drop any prior entry with the same URL — we'll re-insert it at the top
  // with the freshest fields so the suggestion always reflects the latest probe.
  const filtered = existing.filter((c) => c.url !== url);
  const prior = existing.find((c) => c.url === url);
  const merged: RecentWebClip = {
    url,
    addedAt: now,
    // Prefer freshly-supplied values, but fall back to anything we already
    // had so a quick re-add (without a probe) doesn't lose icon metadata.
    displayName: input.displayName ?? prior?.displayName,
    icon: input.icon ?? prior?.icon,
    iconKind: input.iconKind ?? prior?.iconKind,
  };
  const next = [merged, ...filtered].slice(0, MAX_RECENTS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota or privacy mode — recents are best-effort, never block the UX.
  }
  return next;
}

/** Test/admin helper — wipe the local recents list. */
export function clearRecentWebClips(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
